import 'inter-ui/inter.css';
import { createUnifiedTheme, genPageTheme, shapes } from '@backstage/theme';
import './style.css';
import './ag-grid-theme-deepc.css';

const designTokens = {
  borderRadius: {
    small: 4,
    medium: 8,
    large: 12,
  },
  spacing: {
    xs: 4,
    sm: 8,
    mdSm: 12,
    md: 16,
    lg: 24,
    xl: 32,
  },
  shadows: {
    card: '0 2px 4px rgba(0, 0, 0, 0.2)',
    cardHover: '0 4px 8px rgba(0, 0, 0, 0.3)',
    dialog: '0 8px 24px rgba(0, 0, 0, 0.4)',
    sidebar: '0 0 10px rgba(0, 0, 0, 0.5)',
  },
  transitions: {
    short: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    medium: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

const modeTokens = {
  dark: {
    page: '#181829',
    paper: '#212132',
    elevated: '#252538',
    hover: '#373745',
    active: '#424254',
    border: '#4b4b59',
    divider: '#373745',
    textPrimary: '#ffffff',
    textSecondary: '#b7b7bd',
    textDisabled: '#5e5e6b',
    textHint: '#9a9aa3',
    sidebarShadow: '0 0 10px #00000080',
    inputLabelBackground: '#212132',
    tableHeader: '#373745',
    tableHover: '#373745',
    popover: '#373745',
    menu: '#212132',
    buttonText: '#ffffff',
  },
  light: {
    page: '#ffffff',
    paper: '#ffffff',
    elevated: '#dce3e8',
    hover: '#DCE3E880',
    active: '#c8d4db',
    border: '#e0e0e8',
    divider: '#c8d4db',
    textPrimary: '#212132',
    textSecondary: '#555566',
    textDisabled: '#8a8a93',
    textHint: '#6E6E6E',
    sidebarShadow: '1px 0 0 #e0e0e8',
    inputLabelBackground: '#ffffff',
    tableHeader: '#c8d4db',
    tableHover: '#c8d4db',
    popover: '#dce3e8',
    menu: '#dce3e8',
    buttonText: '#212132',
  },
} as const;

type ThemeMode = keyof typeof modeTokens;

const createDeepcComponents = (mode: ThemeMode): any => {
  const colors = modeTokens[mode];
  const primaryMain = mode === 'dark' ? '#9894f9' : '#6C69B1';
  const primaryDark = mode === 'dark' ? '#6C69B1' : '#4a47a3';
  const scrollbarTrack = mode === 'dark' ? '#373745' : '#e0e0e8';
  const scrollbarThumb = mode === 'dark' ? '#4b4b59' : '#a6a6ad';

  return {
    MuiCssBaseline: {
      styleOverrides: `
        *, *::before, *::after { box-sizing: border-box; }
        html { overflow-x: hidden; }
        body[data-theme-mode='${mode}'] {
          background-color: ${colors.page};
          color: ${colors.textPrimary};
        }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: ${scrollbarTrack}; }
        ::-webkit-scrollbar-thumb {
          background: ${scrollbarThumb};
          border-radius: 4px;
          border: 2px solid ${scrollbarTrack};
        }
        ::-webkit-scrollbar-thumb:hover { background: ${colors.active}; }
        * { scrollbar-width: thin; scrollbar-color: ${scrollbarThumb} ${scrollbarTrack}; }
      `,
    },
    // Backstage core components use the flat BackstageOverrides shape
    // (no `styleOverrides` wrapper). Wrapping them in `styleOverrides` — the
    // MUI v5 shape — silently drops the override, matching gate-backstage.
    BackstageContent: {
      root: {
        overflowX: 'hidden',
        padding: designTokens.spacing.lg,
        backgroundColor: colors.page,
        color: colors.textPrimary,
      },
    },
    BackstageHeader: {
      header: {
        backgroundImage: `url('/admin_portal_header.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: designTokens.shadows.card,
      },
      title: {
        fontWeight: 700,
        fontSize: '1.5rem',
      },
      subtitle: {
        color: colors.textSecondary,
      },
    },
    BackstageHeaderLabel: {
      label: {
        color: colors.textSecondary,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
      value: {
        color: colors.textPrimary,
        fontWeight: 600,
      },
    },
    BackstagePage: {
      // NOTE: `createUnifiedTheme` only forwards component overrides that use
      // the MUI v5 `styleOverrides` shape — `transformV5ComponentThemesToV4`
      // checks `"styleOverrides" in component` and silently drops anything
      // else. The flat `{ root: {...} }` shape does NOT work here (despite the
      // older comment claiming otherwise); it was being dropped entirely.
      styleOverrides: {
        root: {
          backgroundColor: colors.page,
          color: colors.textPrimary,
          // Un-pin the header: Backstage's default renders the page as a grid
          // locked to `height: 100vh` with rows `max-content auto 1fr` and
          // `overflow-y: auto`. That makes only the `1fr` content cell scroll
          // internally, so the header row stays pinned. Dropping the fixed
          // viewport height and letting the window scroll as one unit makes the
          // header scroll away instead of staying stuck.
          //
          // Keep the content row (row 3) as `1fr` so it — not the usually-empty
          // subheader row (row 2, `auto`) — absorbs any surplus height when the
          // page is shorter than the viewport. Using `max-content` for row 3
          // instead made row 2 greedy, pushing the Back button / filter bar /
          // content down into the vertical middle on sparse pages (e.g. the
          // Concordance empty state).
          gridTemplateRows: 'max-content auto 1fr',
          height: 'auto',
          minHeight: '100vh',
          overflowY: 'visible',
        },
      },
    },
    BackstageSidebar: {
      drawer: {
        boxShadow: colors.sidebarShadow,
        background: colors.paper,
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        // Backstage's own SidebarItem pins the label to `width: 110px` with
        // overflow/ellipsis/nowrap, which clips "System Manager" et al. The
        // width is what actually truncates; unsetting overflow alone is not
        // enough.
        label: {
          width: 'auto',
          overflow: 'visible',
          textOverflow: 'clip',
          whiteSpace: 'normal',
          // The label is a `Typography variant="subtitle2"`. Backstage's own
          // `label` rule sets no color, so it should inherit from the anchor,
          // which carries navigation.color when unselected and
          // navigation.selectedColor when selected (this is also why the icon
          // — an svg using currentColor — turns white correctly).
          //
          // But `createUnifiedTheme` forwards every `styleOverrides` block into
          // the *v4* JSS theme, and `@backstage/core-components` still renders
          // this label with MUI v4. So the global `MuiTypography.subtitle2`
          // override below lands on it as a JSS rule pinning it to
          // textSecondary. Both it and `label` are single-class selectors, so
          // specificity ties and the winner is whatever JSS injects last —
          // which differs between the dev server and the production bundle.
          // Hence white locally, grey in prod.
          //
          // `&&` compiles under jss-plugin-nested to `.label.label`, raising
          // specificity to (0,2,0) so it beats subtitle2's (0,1,0) no matter
          // the injection order. Do not collapse this to a plain `color`.
          '&&': {
            color: 'inherit',
          },
        },
        secondaryAction: { display: 'none' },
      },
    },
    BackstageInfoCard: {
      header: {
        borderBottom: `1px solid ${colors.hover}`,
        padding: designTokens.spacing.md,
      },
      headerTitle: {
        fontWeight: 600,
        fontSize: '1rem',
      },
      headerSubheader: {
        color: colors.textSecondary,
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.paper,
          boxShadow:
            mode === 'dark' ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
          transition: designTokens.transitions.medium,
        },
        rounded: {
          borderRadius: designTokens.borderRadius.medium,
        },
        elevation1: {
          boxShadow:
            mode === 'dark'
              ? 'none'
              : '0 1px 3px rgba(0,0,0,0.08)',
        },
        elevation2: {
          boxShadow: designTokens.shadows.card,
        },
        elevation3: {
          boxShadow: designTokens.shadows.cardHover,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.medium,
          backgroundColor: colors.paper,
          border: `1px solid ${colors.hover}`,
          transition: designTokens.transitions.medium,
          '&:hover': {
            borderColor: colors.active,
            boxShadow: designTokens.shadows.cardHover,
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: designTokens.spacing.md,
          borderBottom: `1px solid ${colors.hover}`,
        },
        title: {
          fontWeight: 600,
          fontSize: '1rem',
        },
        subheader: {
          color: colors.textSecondary,
          fontSize: '0.875rem',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: designTokens.spacing.md,
          '&:last-child': {
            paddingBottom: designTokens.spacing.md,
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: designTokens.spacing.md,
          borderTop: `1px solid ${colors.hover}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: designTokens.borderRadius.small,
          padding: '6px 16px',
          transition: designTokens.transitions.short,
          color: colors.buttonText,
          '&.Mui-disabled': {
            color:
              mode === 'dark'
                ? 'rgba(255, 255, 255, 0.4)'
                : 'rgba(0, 0, 0, 0.38)',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: designTokens.shadows.card,
          },
        },
        containedPrimary: {
          backgroundColor: primaryMain,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: primaryDark,
          },
        },
        outlined: {
          borderWidth: 1,
          borderColor:
            mode === 'dark'
              ? 'rgba(255, 255, 255, 0.35)'
              : 'rgba(0, 0, 0, 0.23)',
          '&:hover': {
            borderWidth: 1,
            borderColor:
              mode === 'dark'
                ? 'rgba(255, 255, 255, 0.6)'
                : 'rgba(0, 0, 0, 0.45)',
            backgroundColor:
              mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(108, 105, 177, 0.08)',
          },
        },
        sizeSmall: {
          padding: `${designTokens.spacing.xs}px ${designTokens.spacing.sm}px`,
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: `${designTokens.spacing.mdSm}px ${designTokens.spacing.lg}px`,
          fontSize: '1rem',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.small,
          transition: designTokens.transitions.short,
          color: colors.textSecondary,
          '&:hover': {
            backgroundColor: colors.hover,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: designTokens.borderRadius.small,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          colorScheme: mode,
          borderRadius: designTokens.borderRadius.small,
          transition: designTokens.transitions.short,
          color: colors.textPrimary,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.textSecondary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryMain,
            borderWidth: 2,
          },
        },
        notchedOutline: {
          borderColor: mode === 'dark' ? colors.hover : 'rgba(0, 0, 0, 0.23)',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.textSecondary,
          '&.Mui-focused': {
            color: primaryMain,
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: colors.textSecondary,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: colors.textPrimary,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: colors.textSecondary,
          fontSize: '0.875rem',
          fontWeight: 500,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          marginTop: designTokens.spacing.xs,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: colors.textPrimary,
        },
        label: {
          color: colors.textPrimary,
          fontSize: '0.875rem',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          borderRadius: designTokens.borderRadius.small,
        },
        icon: {
          color: colors.textSecondary,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.textSecondary,
          '&.Mui-checked': {
            color: primaryMain,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: colors.textSecondary,
          '&.Mui-checked': {
            color: primaryMain,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase': {
            color: colors.textSecondary,
          },
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: primaryMain,
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: primaryMain,
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: 0,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.medium,
          border: `1px solid ${colors.hover}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.hover}`,
          padding: `${designTokens.spacing.mdSm}px ${designTokens.spacing.md}px`,
          fontSize: '0.875rem',
          wordBreak: 'break-word',
        },
        head: {
          background: colors.tableHeader,
          border: 'none',
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: colors.textSecondary,
        },
        body: {
          color: colors.textPrimary,
          padding: `${designTokens.spacing.mdSm}px ${designTokens.spacing.md}px`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: colors.paper,
          transition: designTokens.transitions.short,
          '&:last-child td': {
            borderBottom: 'none',
          },
        },
        hover: {
          '&:hover': {
            backgroundColor: colors.tableHover,
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: colors.textPrimary,
          },
          '&.Mui-active': {
            color: primaryMain,
          },
        },
        icon: {
          opacity: 0.5,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48,
        },
        flexContainer: {
          minHeight: 48,
        },
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          backgroundColor: primaryMain,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 48,
          padding: `${designTokens.spacing.sm}px ${designTokens.spacing.md}px`,
          transition: designTokens.transitions.short,
          '&:hover': {
            color: colors.textPrimary,
            backgroundColor: 'rgba(152, 148, 249, 0.08)',
          },
          '&.Mui-selected': {
            color: primaryMain,
            fontWeight: 600,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.small,
          fontWeight: 500,
          fontSize: '0.8125rem',
          height: 28,
        },
        outlined: {
          borderColor: colors.hover,
        },
        deleteIcon: {
          color: colors.textSecondary,
          '&:hover': {
            color: colors.textPrimary,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.elevated,
          color: colors.textPrimary,
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: designTokens.borderRadius.small,
          boxShadow: designTokens.shadows.card,
          padding: `${designTokens.spacing.sm}px ${designTokens.spacing.mdSm}px`,
        },
        arrow: {
          color: colors.elevated,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: designTokens.borderRadius.large,
          boxShadow: designTokens.shadows.dialog,
          backgroundColor: colors.paper,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: designTokens.spacing.lg,
          fontSize: '1.25rem',
          fontWeight: 600,
          borderBottom: `1px solid ${colors.hover}`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: designTokens.spacing.lg,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: designTokens.spacing.md,
          borderTop: `1px solid ${colors.hover}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.menu,
          borderRadius: designTokens.borderRadius.medium,
          boxShadow: designTokens.shadows.dialog,
          border: `1px solid ${colors.hover}`,
        },
        list: {
          padding: designTokens.spacing.sm,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.small,
          margin: `${designTokens.spacing.xs}px 0`,
          padding: `${designTokens.spacing.sm}px ${designTokens.spacing.md}px`,
          transition: designTokens.transitions.short,
          '&:hover': {
            backgroundColor: colors.hover,
          },
          '&.Mui-selected': {
            backgroundColor: colors.hover,
            '&:hover': {
              backgroundColor: colors.active,
            },
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.popover,
          borderRadius: designTokens.borderRadius.medium,
          boxShadow: designTokens.shadows.dialog,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.popover,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: colors.paper,
          borderRadius: `${designTokens.borderRadius.medium}px !important`,
          border: `1px solid ${colors.hover}`,
          marginBottom: designTokens.spacing.sm,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: `0 0 ${designTokens.spacing.sm}px 0`,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: `0 ${designTokens.spacing.md}px`,
          minHeight: 56,
          '&.Mui-expanded': {
            minHeight: 56,
            borderBottom: `1px solid ${colors.hover}`,
          },
        },
        content: {
          gap: designTokens.spacing.lg,
          alignItems: 'center',
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: designTokens.spacing.md,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: { fontWeight: 700 },
        h2: { fontWeight: 700 },
        h3: { fontWeight: 600 },
        h4: { fontWeight: 600 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 },
        subtitle1: { fontWeight: 500 },
        subtitle2: {
          fontWeight: 500,
          color: colors.textSecondary,
        },
        body2: {
          color: colors.textSecondary,
        },
        caption: {
          color: colors.textHint,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: colors.hover,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.small,
          height: 6,
          backgroundColor: colors.hover,
        },
        bar: {
          borderRadius: designTokens.borderRadius.small,
        },
      },
    },
  };
};

// ── Dark theme ────────────────────────────────────────────────────────────────

export const deepcTheme = createUnifiedTheme({
  fontFamily: 'Inter',
  palette: {
    type: 'dark',
    mode: 'dark',
    primary: { light: '#9894f9', dark: '#6C69B1', main: '#9894f9', contrastText: '#fff' },
    secondary: { light: '#A6A6AD', dark: '#A6A6AD', main: '#A6A6AD', contrastText: '#fff' },
    error: { light: '#F05252', dark: '#F05252', main: '#F05252', contrastText: '#fff' },
    warning: { light: '#E3A008', dark: '#E3A008', main: '#E3A008', contrastText: '#fff' },
    info: { light: '#3F83F8', dark: '#3F83F8', main: '#3F83F8', contrastText: '#fff' },
    success: { light: '#0E9F6E', dark: '#0E9F6E', main: '#0E9F6E' },
    background: { default: '#181829', paper: '#212132' },
    text: {
      primary: '#ffffff',
      secondary: '#b7b7bd',
      disabled: '#5e5e6b',
    },
    errorBackground: '#212132',
    warningBackground: '#8f5e15',
    infoBackground: '#343b58',
    border: '#E6E6E6',
    textContrast: '#FFFFFF',
    textVerySubtle: '#DDD',
    textSubtle: '#6E6E6E',
    highlight: '#FFFBCC',
    errorText: '#CA001B',
    infoText: '#004e8a',
    warningText: '#000000',
    linkHover: '#2196F3',
    link: '#0A6EBE',
    gold: '#FFD600',
    bursts: {
      fontColor: '#FEFEFE',
      slackChannelText: '#ddd',
      backgroundColor: { default: '#9894f9' },
      gradient: { linear: 'linear-gradient(-137deg, #9894f9 0%, #6C69B1 100%)' },
    },
    banner: {
      info: '#2E77D0',
      error: '#E22134',
      text: '#FFFFFF',
      link: '#000000',
      closeButtonColor: '#FFFFFF',
      warning: '#FF9800',
    },
    pinSidebarButton: { icon: '#FFFFFF', background: '#404040' },
    tabbar: { indicator: '#9894f9' },
    navigation: {
      background: '#212132',
      indicator: '#9894f9',
      color: '#b7b7bd',
      selectedColor: '#fff',
      navItem: { hoverBackground: '#373745' },
      submenu: { background: '#212132' },
    },
    status: {
      ok: '#31c48d',
      warning: '#E3A008',
      error: '#F05252',
      pending: '#08b0e3',
      running: '#08b0e3',
      aborted: '#F05252',
    },
  },
  defaultPageTheme: 'tool',
  pageTheme: {
    home: genPageTheme({ colors: ['#9894f9', '#6C69B1'], shape: shapes.wave, options: { fontColor: '#fff' } }),
    documentation: genPageTheme({ colors: ['#9894f9', '#6C69B1'], shape: shapes.wave2, options: { fontColor: '#fff' } }),
    tool: {
      colors: ['#9894f9', '#6C69B1'],
      shape: shapes.round,
      backgroundImage: `url('/admin_portal_header.png')`,
      fontColor: '#fff',
    },
    service: genPageTheme({ colors: ['#9894f9', '#6C69B1'], shape: shapes.wave, options: { fontColor: '#fff' } }),
    website: genPageTheme({ colors: ['#9894f9', '#6C69B1'], shape: shapes.wave, options: { fontColor: '#fff' } }),
    library: genPageTheme({ colors: ['#9894f9', '#6C69B1'], shape: shapes.wave, options: { fontColor: '#fff' } }),
    other: genPageTheme({ colors: ['#9894f9', '#6C69B1'], shape: shapes.wave, options: { fontColor: '#fff' } }),
    app: genPageTheme({ colors: ['#9894f9', '#6C69B1'], shape: shapes.wave, options: { fontColor: '#fff' } }),
    apis: genPageTheme({ colors: ['#9894f9', '#6C69B1'], shape: shapes.wave, options: { fontColor: '#fff' } }),
  },
  components: createDeepcComponents('dark'),
});

// ── Light theme ───────────────────────────────────────────────────────────────

export const deepcLightTheme = createUnifiedTheme({
  fontFamily: 'Inter',
  palette: {
    // Both type (MUI v4) and mode (MUI v5) are required by createUnifiedTheme
    type: 'light',
    mode: 'light',
    primary: { light: '#9894f9', dark: '#4a47a3', main: '#6C69B1', contrastText: '#fff' },
    secondary: { light: '#A6A6AD', dark: '#A6A6AD', main: '#A6A6AD', contrastText: '#fff' },
    error: { light: '#F05252', dark: '#F05252', main: '#F05252', contrastText: '#fff' },
    warning: { light: '#E3A008', dark: '#E3A008', main: '#E3A008', contrastText: '#fff' },
    info: { light: '#3F83F8', dark: '#3F83F8', main: '#3F83F8', contrastText: '#fff' },
    success: { light: '#0E9F6E', dark: '#0E9F6E', main: '#0E9F6E' },
    background: { default: '#ffffff', paper: '#DCE3E880' },
    text: {
      primary: 'rgba(0,0,0,0.87)',
      secondary: 'rgba(0,0,0,0.87)',
    },
    errorBackground: '#fdecea',
    warningBackground: '#fff8e1',
    infoBackground: '#e8f0fe',
    border: '#e0e0e8',
    textContrast: '#000000',
    textVerySubtle: '#DDD',
    textSubtle: '#6E6E6E',
    highlight: '#FFFBCC',
    errorText: '#CA001B',
    infoText: '#004e8a',
    warningText: '#000000',
    linkHover: '#2196F3',
    link: '#0A6EBE',
    gold: '#FFD600',
    bursts: {
      fontColor: '#FEFEFE',
      slackChannelText: '#ddd',
      backgroundColor: { default: '#6C69B1' },
      gradient: { linear: 'linear-gradient(-137deg, #9894f9 0%, #6C69B1 100%)' },
    },
    banner: {
      info: '#3F83F8',
      error: '#F05252',
      text: '#FFFFFF',
      link: '#000000',
      closeButtonColor: '#FFFFFF',
      warning: '#E3A008',
    },
    pinSidebarButton: { icon: '#181818', background: '#BDBDBD' },
    tabbar: { indicator: '#6C69B1' },
    navigation: {
      background: '#ffffff',
      indicator: '#6C69B1',
      color: '#555566',
      selectedColor: '#212132',
      navItem: { hoverBackground: '#DCE3E880' },
      submenu: { background: '#DCE3E880' },
    },
    status: {
      ok: '#0E9F6E',
      warning: '#E3A008',
      error: '#F05252',
      pending: '#3F83F8',
      running: '#3F83F8',
      aborted: '#F05252',
    },
  },
  defaultPageTheme: 'tool',
  pageTheme: {
    home: genPageTheme({ colors: ['#6C69B1', '#9894f9'], shape: shapes.wave }),
    documentation: genPageTheme({ colors: ['#6C69B1', '#9894f9'], shape: shapes.wave2 }),
    tool: genPageTheme({ colors: ['#6C69B1', '#9894f9'], shape: shapes.round }),
    service: genPageTheme({ colors: ['#6C69B1', '#9894f9'], shape: shapes.wave }),
    website: genPageTheme({ colors: ['#6C69B1', '#9894f9'], shape: shapes.wave }),
    library: genPageTheme({ colors: ['#6C69B1', '#9894f9'], shape: shapes.wave }),
    other: genPageTheme({ colors: ['#6C69B1', '#9894f9'], shape: shapes.wave }),
    app: genPageTheme({ colors: ['#6C69B1', '#9894f9'], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#6C69B1', '#9894f9'], shape: shapes.wave }),
  },
  components: createDeepcComponents('light'),
});
